import { useState, useEffect, useCallback, useRef } from 'react';
import { resolveSession } from '../services/sessionClient';
import { parseSessionResponse } from '../schemas/sessionResponse.schema';

const MAX_RETRIES = 3;
const BACKOFF_MS = [1000, 2000, 4000];

// Owner-secret persistence. The secret is stored per session so the SAME device
// can re-present it on later loads to unlock the customer's KYC/DOB (ownership
// proof). This TTL is the client-side hygiene bound for pruning stale secrets;
// it is set to the backend's MAXIMUM session lifetime (MAX_TTL_HOURS = 72h in
// mobile-compliance-session.mjs), not the 24h default. The backend clamps a
// session's TTL to 1-72h, and the resolve response does not expose the exact
// expiresAt, so pruning at the 72h ceiling guarantees we never drop a secret
// while the session could still be valid on the server. Once a session is truly
// expired the backend returns 410 / kyc=null anyway, so a lingering client
// secret is harmless. If the resolve ever returns `expiresAt`, prefer keying the
// expiry off that exact value instead of this ceiling.
const OWNER_SECRET_PREFIX = 'viam_owner_secret_';
const OWNER_SECRET_TTL_MS = 72 * 60 * 60 * 1000; // 72h (backend MAX_TTL_HOURS)

/**
 * Parse a stored owner-secret entry. New entries are JSON `{ secret, ts }`.
 * Older entries were the raw secret string (no timestamp) — treated as `legacy`
 * with an unknown age so they are never expired blindly (that would break a
 * still-valid session).
 */
const readSecretEntry = raw => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.secret === 'string'
    ) {
      return { secret: parsed.secret, ts: Number(parsed.ts) || 0 };
    }
  } catch (e) {
    // Not JSON → legacy raw-string secret.
  }
  return { secret: String(raw), ts: 0, legacy: true };
};

/**
 * Remove only EXPIRED, datable owner secrets (older than the TTL). Legacy
 * entries (unknown age) are left intact so a still-valid session is not broken.
 */
const pruneExpiredOwnerSecrets = () => {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key || key.indexOf(OWNER_SECRET_PREFIX) !== 0) continue;
      const entry = readSecretEntry(localStorage.getItem(key));
      if (entry && entry.ts && now - entry.ts > OWNER_SECRET_TTL_MS) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // localStorage unavailable — ignore.
  }
};

/**
 * Persist/refresh this session's owner secret (when the binding resolve returns
 * one) and return the effective secret to use, or null if none/expired. Prunes
 * other sessions' expired secrets as a side effect so they don't accumulate.
 */
const persistOwnerSecret = (sessionId, freshSecret) => {
  const key = `${OWNER_SECRET_PREFIX}${sessionId}`;
  try {
    pruneExpiredOwnerSecrets();
    const now = Date.now();
    if (freshSecret) {
      localStorage.setItem(key, JSON.stringify({ secret: freshSecret, ts: now }));
    }
    const entry = readSecretEntry(localStorage.getItem(key));
    if (!entry) return null;
    // This session's secret expired → drop it (device is no longer the owner).
    if (entry.ts && now - entry.ts > OWNER_SECRET_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    // Migrate a legacy raw secret to the timestamped format so it can expire.
    if (entry.legacy) {
      localStorage.setItem(key, JSON.stringify({ secret: entry.secret, ts: now }));
    }
    return entry.secret;
  } catch (e) {
    return freshSecret || null;
  }
};

/**
 * Resolve an opaque compliance session into its context.
 *
 * States: idle -> loading -> resolved | error
 *
 * Behavior:
 *  - Retries up to 3 times with exponential backoff (1s, 2s, 4s) ONLY for
 *    transient errors (network, timeout). Terminal errors (invalid, expired,
 *    consumed, schema) do not retry.
 *  - Validates every response with Zod. A schema failure is treated as an
 *    'invalid' error and the malformed data is never exposed.
 *  - Never navigates away on failure; surfaces a typed error for the UI.
 *
 * @param {{ baseUrl: string, token: string, sessionId: string }} params
 */
export const useSessionResolver = ({ baseUrl, token, sessionId }) => {
  const [status, setStatus] = useState('idle');
  const [session, setSession] = useState(null);
  const [ownerSecret, setOwnerSecret] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [attemptKey, setAttemptKey] = useState(0);
  const timerRef = useRef(null);

  const isTransient = (type) => type === 'network' || type === 'timeout';

  const retry = useCallback(() => {
    setRetryCount(0);
    setAttemptKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!sessionId || !baseUrl) {
      setStatus('error');
      setError({ type: 'invalid', message: 'Missing session identifier' });
      return undefined;
    }

    let cancelled = false;

    const attempt = async (tryIndex) => {
      setStatus('loading');
      setError(null);

      try {
        const raw = await resolveSession({ baseUrl, token, sessionId });
        if (cancelled) return;

        const parsed = parseSessionResponse(raw);
        if (!parsed.ok) {
          // Schema validation failed — discard malformed data, do not render.
          setStatus('error');
          setError({ type: 'invalid', message: 'Invalid session response' });
          return;
        }

        // Owner secret: returned ONLY on the first (binding) resolve. Persist it
        // per session (with a 24h TTL) so this device can present it on later
        // loads. A different device never receives it, so it can't unlock the
        // customer's KYC data. Expired secrets are pruned so they don't
        // accumulate; secrets still within the TTL are preserved, so reopening a
        // valid session keeps owner access.
        const effectiveSecret = persistOwnerSecret(
          sessionId,
          parsed.data.ownerSecret
        );
        setOwnerSecret(effectiveSecret);

        setSession(parsed.data);
        setStatus('resolved');
      } catch (err) {
        if (cancelled) return;

        const type = err.type || 'network';

        if (isTransient(type) && tryIndex < MAX_RETRIES - 1) {
          const delay = BACKOFF_MS[tryIndex] || 4000;
          setRetryCount(tryIndex + 1);
          timerRef.current = setTimeout(() => {
            if (!cancelled) attempt(tryIndex + 1);
          }, delay);
          return;
        }

        setStatus('error');
        setError({ type, message: err.message });
      }
    };

    attempt(0);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [baseUrl, token, sessionId, attemptKey]);

  return { status, session, ownerSecret, error, retry, retryCount };
};

export default useSessionResolver;
